/**
 * Camera Preview Component
 *
 * Displays live camera feed with optional pose detection overlay
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { debugLog } from '../../../../../common/debugUtilities';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    width: '100%',
    maxWidth: 640,
    maxHeight: 'calc(100vh - 250px)', // Reserve space for controls and header
    margin: '0 auto',
    backgroundColor: '#000',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  video: {
    width: '100%',
    height: 'auto',
    maxHeight: 'calc(100vh - 250px)', // Prevent video from extending beyond viewport
    display: 'block',
    objectFit: 'contain'
    // No mirroring for back camera - natural view for filming baby
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  },
  placeholder: {
    width: '100%',
    aspectRatio: '16 / 9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#333',
    color: '#fff'
  }
}));

interface CameraPreviewProps {
  stream: MediaStream | null;
  onVideoReady?: (videoElement: HTMLVideoElement) => void;
  placeholder?: string;
}

export default function CameraPreview({
  stream,
  onVideoReady,
  placeholder = 'Camera preview will appear here'
}: CameraPreviewProps): JSX.Element {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSetupRef = useRef(false);

  // Set up video stream
  useEffect(() => {
    if (videoRef.current && stream && !isSetupRef.current) {
      isSetupRef.current = true;
      debugLog('[CameraPreview] Setting up video stream:', stream.id);
      debugLog('[CameraPreview] Video tracks:', stream.getVideoTracks().map(t => t.label));

      videoRef.current.srcObject = stream;

      // Wait for video to be ready
      const handleLoadedMetadata = () => {
        if (videoRef.current) {
          debugLog('[CameraPreview] Video metadata loaded');
          debugLog('[CameraPreview] Video dimensions:', {
            videoWidth: videoRef.current.videoWidth,
            videoHeight: videoRef.current.videoHeight,
            clientWidth: videoRef.current.clientWidth,
            clientHeight: videoRef.current.clientHeight
          });

          // Scroll container into view to ensure camera is visible
          if (containerRef.current) {
            containerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            debugLog('[CameraPreview] Scrolled camera into view');
          }

          // Explicitly play the video
          videoRef.current.play()
            .then(() => {
              debugLog('[CameraPreview] Video playing successfully');
            })
            .catch(err => {
              console.error('[CameraPreview] Failed to play video:', err);
            });

          if (onVideoReady) {
            onVideoReady(videoRef.current);
          }
        }
      };

      const handleCanPlay = () => {
        debugLog('[CameraPreview] Video can play');
      };

      videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoRef.current.addEventListener('canplay', handleCanPlay);

      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
          videoRef.current.removeEventListener('canplay', handleCanPlay);
        }
        isSetupRef.current = false;
      };
    } else if (stream && !videoRef.current) {
      debugLog('[CameraPreview] Waiting for video ref...');
    } else if (!stream) {
      debugLog('[CameraPreview] No stream available');
      isSetupRef.current = false;
    }
  }, [stream, onVideoReady]);

  // Clean up stream when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  if (!stream) {
    return (
      <div ref={containerRef}>
        <Box className={classes.container}>
          <Box className={classes.placeholder}>
            <Typography variant="body2">{placeholder}</Typography>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <Box className={classes.container}>
        <video
          ref={videoRef}
          className={classes.video}
          autoPlay
          playsInline
          muted
        />
      </Box>
    </div>
  );
}
